import React, { useState, useEffect } from 'react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { Icon } from '@iconify/react';
import { getTraineeAnalytics } from '../../../services/traineeService';
import { toast } from 'react-toastify';

function TraineeAnalyticsCertificatesTab({ traineeId }) {
  const [loading, setLoading] = useState(false);
  const [certificatesData, setCertificatesData] = useState([]);

  const fetchCertificatesData = async () => {
    try {
      setLoading(true);
      const response = await getTraineeAnalytics({ trainee_id: traineeId });
      // console.log('certificates Data---------------------:', response.data.certificates);

      if (response.success) { 
        setCertificatesData(response.data.certificates || []);
      } else {
        toast.error(response.message || 'Failed to fetch certificates data');
      }
    } catch (error) {
      console.error('Error fetching certificates data:', error);
      toast.error('Something went wrong while fetching certificates data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (traineeId) {
      fetchCertificatesData();
    }
  }, [traineeId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewCertificate = (certificate) => {
    if (!certificate.certificate_url) {
      toast.warning('Certificate not available');
      return;
    }

    // Create a link element
    const link = document.createElement('a');
    link.href = certificate.certificate_url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Add download attribute with filename
    const fileName = `${certificate.course_name}_${certificate.certificate_name}.pdf`;
    link.download = fileName;
    
    // Trigger click
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[220px] rounded-2xl border border-gray-100 bg-gray-50/40">
        <Loader />
      </div>
    );
  }

  if (!certificatesData || certificatesData.length === 0) {
    return <NoData message="No certificates found" />;
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[720px] w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="text-left text-xs font-semibold text-gray-600">
              <th className="px-4 py-3 w-[90px]">SL No.</th>
              <th className="px-4 py-3">Certificate</th>
              <th className="px-4 py-3 w-[200px]">Issue Date</th>
              <th className="px-4 py-3 w-[120px] text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {certificatesData.map((certificate, index) => (
              <tr key={certificate.id} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                <td className="px-4 py-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate" title={certificate.course_name || ''}>
                      {certificate.course_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate" title={certificate.certificate_name || ''}>
                      {certificate.certificate_name}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{formatDate(certificate.issued_date)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                      title="Download Certificate"
                      onClick={() => handleViewCertificate(certificate)}
                    >
                      <Icon icon="fluent:document-pdf-24-regular" width="18" height="18" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TraineeAnalyticsCertificatesTab;